import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import StepButton, { StepButtonSize } from './StepButton';
import RollingNumber from '../RollingNumber';

const Wrapper = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;

  &.size-small {
    gap: 4px;
  }
`;

/** Rolls like an odometer when stepped - see RollingNumber. */
const Quantity = styled(RollingNumber)`
  min-width: 48px;
  text-align: center;
  font-size: 24px;
  font-weight: 800;

  .size-small & {
    min-width: 32px;
    font-size: 20px;
  }
`;

interface Props {
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Lowest value "−" may reach. Defaults to 0. */
  min?: number;
  /** `small` fits a card footer; `medium` (default) is the cart row size. */
  size?: StepButtonSize;
}

/**
 * "− n +" used in the cart, on upsells and in the item detail. Each side is a
 * StepButton, so a touch re-renders one round button, not the whole stepper.
 */
const QuantityStepper: FC<Props> = ({
  quantity,
  onIncrement,
  onDecrement,
  min = 0,
  size = 'medium',
}) => {
  return (
    <Wrapper className={`size-${size}`}>
      <StepButton
        glyph="−"
        size={size}
        onClick={onDecrement}
        disabled={quantity <= min}
        testId="epos-stepper-dec"
      />
      <Quantity value={quantity} testId="epos-stepper-qty" />
      <StepButton glyph="+" size={size} onClick={onIncrement} testId="epos-stepper-inc" />
    </Wrapper>
  );
};

export default memo(QuantityStepper);
